import React, { useState, useEffect } from 'react';
import './MiddleSection.css';
import { fetchDataFromAPI } from '../common/fetchDataFromAPI';
import { GPU_SERVERS } from '../data';
import Spinner from './Spinner';

function getSelectedServer(formData) {
  const server = GPU_SERVERS.find(s => s.id === formData.serverType);
  if (!server) return null;
  if (server.subModels) {
    return server.subModels.find(sub => sub.id === formData.serverSubModel) || null;
  }
  return server;
}

function MiddleSection({ formData,
  setFormData,
  setPlotData,
  setIsAuthenticated,
  setLogColor,
  setLogMessage,
  measurementSystem,
  advancedUser}) {

  const [selectedHRU, setSelectedHRU] = useState("HRU_WATER_3");
  const [selectedCPU, setSelectedCPU] = useState("SP5");
  const [timeoutId, setTimeoutId] = useState(null);

  const handleFormSubmit = async () => {
    console.log("API FIRED", formData);

    try {
      let params;
      let url;

      if (formData.calculationMode === 'cpu') {
        params = {
          Tcase: formData.Tcase,
          CPU_Power: formData.CPU_Power,
          Num_CPU: formData.Num_CPU,
          CPUType: formData.CPUType,
          HruType: formData.HruType,
          MeasurementSystem: measurementSystem,
        };
        url = 'calculateInletWater';
      } else {
        const server = getSelectedServer(formData);
        if (!server) return;
        const totalPowerWatts = Math.round(server.coolingPowerKW * 1000 * formData.numServers);
        params = {
          totalPower: totalPowerWatts,
          saturationTemperature: server.tsat,
          HruType: formData.HruType,
          MeasurementSystem: measurementSystem,
        };
        url = 'calculateServer';
      }

      const method = 'GET';
      const token = localStorage.getItem('zuta');

      const data = await fetchDataFromAPI(url, method, params, token);

      if (data && data.error) {
        console.log('Error: ', data);

        if (data.error === "invalid_token" || data.error === "invalid_grant") {
          setPlotData(null);
          setIsAuthenticated(false);
          setLogColor("red");
          setLogMessage("Access token expired");
        } else {
          setLogColor("red");
          setLogMessage(data.message);
          setPlotData(null);
        }
      } else {
        console.log('API SUCCESS: ', data);
        setPlotData(data);
      }
    } catch (error) {
      console.log('Error:', error);
    }
  };

  const handleImageClick = (keyName, imageName) => {
    let newData = { ...formData };

    if (keyName === 'HruType') {
      if (selectedHRU === imageName) {
        setSelectedHRU(null);
        newData = { ...newData, [keyName]: '' };
      } else {
        setSelectedHRU(imageName);
        newData = { ...newData, [keyName]: imageName };
      }
    } else if (keyName === 'CPUType') {
      if (selectedCPU === imageName) {
        setSelectedCPU(null);
        newData = { ...newData, [keyName]: '' };
      } else {
        setSelectedCPU(imageName);
        newData = { ...newData, [keyName]: imageName };
      }
    }

    setFormData(newData);
  };

  const handleServerImageClick = (serverId) => {
    setFormData(prev => {
      if (prev.serverType === serverId) {
        return { ...prev, serverType: '', serverSubModel: '' };
      }
      return { ...prev, serverType: serverId, serverSubModel: '' };
    });
  };

  const handleSubModelClick = (subModelId) => {
    setFormData(prev => {
      if (prev.serverSubModel === subModelId) {
        return { ...prev, serverSubModel: '' };
      }
      return { ...prev, serverSubModel: subModelId };
    });
  };

  // CPU mode validation & auto-submit
  useEffect(() => {
    if (formData.calculationMode !== 'cpu') return;

    const isValid =
      typeof formData.CPUType === 'string' && formData.CPUType.trim() !== '' &&
      typeof formData.HruType === 'string' && formData.HruType.trim() !== '' &&
      typeof formData.MeasurementSystem === 'string' && formData.MeasurementSystem.trim() !== '' &&
      !isNaN(parseFloat(formData.Tcase)) && parseFloat(formData.Tcase) > 0 &&
      !isNaN(parseFloat(formData.CPU_Power)) && parseFloat(formData.CPU_Power) > 0 &&
      !isNaN(parseFloat(formData.Num_CPU)) && parseFloat(formData.Num_CPU) > 0;

    if (isValid) {
      clearTimeout(timeoutId);
      setTimeoutId(setTimeout(handleFormSubmit, 500));
    } else {
      console.log("CPU mode: not valid");
    }
    // eslint-disable-next-line
  }, [formData.CPUType, formData.HruType, formData.Tcase, formData.CPU_Power, formData.Num_CPU, formData.MeasurementSystem, formData.calculationMode]);

  // GPU mode validation & auto-submit
  useEffect(() => {
    if (formData.calculationMode !== 'gpu') return;

    const hasServer = formData.serverType && formData.serverType.trim() !== '';
    const serverObj = GPU_SERVERS.find(s => s.id === formData.serverType);
    const needsSubModel = serverObj && serverObj.subModels;
    const hasSubModel = formData.serverSubModel && formData.serverSubModel.trim() !== '';
    const hasHru = typeof formData.HruType === 'string' && formData.HruType.trim() !== '';
    const hasServers = !isNaN(parseFloat(formData.numServers)) && parseFloat(formData.numServers) > 0;

    const isValid = hasServer && hasHru && hasServers && (!needsSubModel || hasSubModel);

    if (isValid) {
      clearTimeout(timeoutId);
      setTimeoutId(setTimeout(handleFormSubmit, 500));
    } else {
      console.log("GPU mode: not valid");
    }
    // eslint-disable-next-line
  }, [formData.serverType, formData.serverSubModel, formData.numServers, formData.HruType, formData.MeasurementSystem, formData.calculationMode]);

  // HRU images (filter out RDC)
  const HRUimages = require.context('../pics/HRU', false, /\.(jpg|jpeg|png)$/);
  const HRUimageKeys = HRUimages.keys();
  const HRUKeysCleaned = HRUimageKeys.map((key) => key.replace('./', '').replace(/\.(png|jpg|jpeg)$/, ''));
  const HRUKeysFiltered = HRUKeysCleaned.filter(key => key !== 'HRU_RDC');

  // CPU images
  const CPUimages = require.context('../pics/CPU', false, /\.(jpg|jpeg|png)$/);
  const CPUimageKeys = CPUimages.keys();
  const CPUKeysCleaned = CPUimageKeys.map((key) => key.replace('./', '').replace(/\.(png|jpg|jpeg)$/, ''));

  return (
    <div className={`middle-section col-lg-5 col-md-12 col-sm-12 mb-3`} style={{display: advancedUser ? 'none' : 'flex'}}>
      <div className="formContent">
        {/* HRU Selection */}
        <div className='formRowCtr'>
          <label>Choose HRU:</label>
          <div className='formRow'>
            {HRUKeysFiltered.map((key) => (
              <div className="formImgCtr" key={key}>
                <div className={`formImg ${selectedHRU === key ? 'hruFormSelected' : ''} ${key === 'EOR' ? 'hruDisabled' : ''}`}
                  style={{ backgroundImage: `url(build/pics/HRU/${key}.png)` }}
                  onClick={() => key !== 'EOR' && handleImageClick('HruType', key)}>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CPU / GPU Mode Toggle */}
        <div className='formRowCtr'>
          <label>Calculation Mode:</label>
          <div className='modeToggle'>
            <label className={`modeOption ${formData.calculationMode === 'cpu' ? 'modeActive' : ''}`}>
              <input type="radio" name="calcMode" value="cpu"
                checked={formData.calculationMode === 'cpu'}
                onChange={() => setFormData(prev => ({...prev, calculationMode: 'cpu'}))} />
              CPU
            </label>
            <label className={`modeOption ${formData.calculationMode === 'gpu' ? 'modeActive' : ''}`}>
              <input type="radio" name="calcMode" value="gpu"
                checked={formData.calculationMode === 'gpu'}
                onChange={() => setFormData(prev => ({...prev, calculationMode: 'gpu'}))} />
              GPU Server
            </label>
          </div>
        </div>

        {/* CPU Mode Section */}
        {formData.calculationMode === 'cpu' && (
          <>
            <div className='formRowCtr'>
              <label>Choose CPU:</label>
              <div className='formRow'>
                {CPUKeysCleaned.map((key) => (
                  <div className="formImgCtr" key={key}>
                    <div className={`formImg ${selectedCPU === key ? 'cpuFormSelected' : ''}`}
                      style={{ backgroundImage: `url(build/pics/CPU/${key}.png)` }}
                      onClick={() => handleImageClick('CPUType', key)}>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='formRowCtr'>
              <label>System Data:</label>
              <div className='fieldsInput'>
                <div className='textFieldsForm'>
                  <Spinner step={5} min={0} max={1000} name="Num_CPU" title="Number of CPUs:" formData={formData} setFormData={setFormData} />
                  <Spinner step={5} min={150} max={9999} name="CPU_Power" title="CPU Power (Per CPU) [W]:" formData={formData} setFormData={setFormData} />
                  <Spinner step={1} min={40} max={200} name="Tcase" title="Required case Temperature:" formData={formData} setFormData={setFormData} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* GPU Server Mode Section */}
        {formData.calculationMode === 'gpu' && (
          <>
            <div className='formRowCtr'>
              <label>Choose GPU Server:</label>
              <div className='formRow'>
                {GPU_SERVERS.map((server) => (
                  <div className="formImgCtr" key={server.id}>
                    <div className={`formImg ${formData.serverType === server.id ? 'gpuFormSelected' : ''}`}
                      style={{ backgroundImage: `url(build/pics/GPU/${server.image}.png)` }}
                      onClick={() => handleServerImageClick(server.id)}>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sub-model selection for servers that have variants */}
            {formData.serverType && GPU_SERVERS.find(s => s.id === formData.serverType)?.subModels && (
              <div className='formRowCtr'>
                <label>Choose Variant:</label>
                <div className='subModelRow'>
                  {GPU_SERVERS.find(s => s.id === formData.serverType).subModels.map((sub) => (
                    <button
                      key={sub.id}
                      className={`subModelBtn ${formData.serverSubModel === sub.id ? 'subModelActive' : ''}`}
                      onClick={() => handleSubModelClick(sub.id)}>
                      {sub.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className='formRowCtr'>
              <label>System Data:</label>
              <div className='fieldsInput'>
                <div className='textFieldsForm'>
                  <Spinner step={1} min={1} max={500} name="numServers" title="Number of Servers:" formData={formData} setFormData={setFormData} />
                </div>
              </div>
            </div>
          </>
        )}

        <div className='centerButton'>
          <button onClick={() => handleFormSubmit()}>Calculate</button>
        </div>
      </div>
    </div>
  );
}

export default MiddleSection;
